import ChangePlan from "interface/ChangePlan";
import { SignUpResponse, SubscriptionSetupData } from "../models/SignUpResponse";
import { fetchApiData } from "../ApiHelper";
import EndpointProps from "interface/EndpointProps";
import { get, post } from "../ApiClient";
import UpdatePayment from "interface/UpdatePayment";
import { DefaultApiResponse } from "../models/response/DefaultApiResponse";
import UserSubscription from "interface/UserSubscription";
import SubscriptionInvoice from "interface/SubscriptionInvoice";
import SubscriptionChangePreview from "interface/SubscriptionChangePreview";
import SubscriptionChangeResult from "interface/SubscriptionChangeResult";
import ResumeSubscriptionResult from "interface/ResumeSubscriptionResult";
import AccountType from "interface/AccountType";

/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/**
 * Updates the subscription plan for a user.
 *
 * @param {Object} params - The parameters for updating the subscription plan.
 * @param {string} params.tag - The tag associated with the endpoint.
 * @param {ChangePlan} params.data - The data payload containing the new subscription plan details.
 * @returns {Promise<{ data: SignUpResponse | null; error: string | null }>} A promise that resolves to an object containing the response data or an error message.
 *
 * @throws {Error} Throws an error if the fetch operation fails.
 */
export async function updateSubscriptionPlan({
    tag,
    data,
}: EndpointProps & { data: ChangePlan }): Promise<{ data: SignUpResponse | null; error: string | null }> {
    if (!data) {
        return {
            data: null,
            error: "Invalid change plan request: Missing data payload.",
        };
    }

    const result = await fetchApiData<SignUpResponse>(
        tag,
        () =>
            post<SignUpResponse>({
                tag,
                url: "/subscription/change/plan",
                body: data,
            }),
        (responseData) => responseData
    );

    if (result.response.status === "error") {
        return {
            data: null,
            error: result.response.message || "An error occurred while updating plan.",
        };
    }

    return {
        ...result.response.data || null,
        error: null,
    };
}

/**
 * Updates the subscription payment information.
 *
 * @param {EndpointProps & { data: UpdatePayment }} param0 - The endpoint properties and payment update data.
 * @param {string} param0.tag - The tag for the endpoint.
 * @param {UpdatePayment} param0.data - The payment update data.
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating whether the update was successful.
 * @throws {Error} - Throws an error if the data payload is missing.
 */
export async function updateSubscriptionPayment({ tag, data }: EndpointProps & { data: UpdatePayment }): Promise<boolean> {
    if (!data) {
        throw new Error ('Invalid payment update request: Missing data payload.')
    }

    const result = await fetchApiData<DefaultApiResponse>(tag,
        () => post<DefaultApiResponse>({ tag, url: '/subscription/payment', body: data }),
        (responseData) => responseData
    )

    return result.response.status === 'success'
}

/**
 * Fetches subscription data from the API.
 *
 * @param {EndpointProps} param0 - The endpoint properties.
 * @param {string} param0.tag - The tag used for fetching the subscription data.
 * @returns {Promise<{ success: boolean; data?: UserSubscription }>} A promise that resolves to an object containing the success status and the subscription data.
 */
export async function fetchSubscriptionData({ tag }: EndpointProps): Promise<{ success: boolean; data?: UserSubscription }> {
    const result = await fetchApiData<DefaultApiResponse<UserSubscription>>(tag,
        () => get<DefaultApiResponse<UserSubscription>>({ tag, url: `/subscription`}),
        (responseData) => responseData.data
    )

    return {
        success: result.response.status === 'success',
        data: result.response.data
    }
}

/**
 * Fetches subscription invoices from the API.
 * This function fetches subscription invoices based on the specified status.
 * 
 * @param {EndpointProps & { status: 'succeeded' | 'processing' | 'canceled' | 'failed' }} param0 - The endpoint properties and invoice status.
 * @param {string} param0.tag - The tag used for fetching the subscription invoices.
 * @param {'succeeded' | 'processing' | 'canceled' | 'failed'} param0.status - The status of the invoices to fetch.
 * 
 * @returns {Promise<{ success: boolean; data?: SubscriptionInvoice[]; error?: string | null}>} A promise that resolves to an object containing the success status, the invoice data, and an error message if applicable.
 * 
 * @throws {Error} Throws an error if the fetch operation fails.
 * 
 * @example
 * const { success, data, error } = await getSubscriptionInvoices({ tag: 'SubscriptionInvoices', status: 'succeeded' })
 * if (success) {
 *    console.log('Invoices:', data)
 * } else {
 *   console.error('Failed to fetch invoices:', error)
 * }
 */
export async function getSubscriptionInvoices({ tag, status }: EndpointProps & { status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' }): Promise<{ success: boolean; data?: SubscriptionInvoice[], error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<SubscriptionInvoice[]>>(tag,
        () => get<DefaultApiResponse<SubscriptionInvoice[]>>({ tag, url: `/subscription/invoices?status=${status}`}),
        (responseData) => responseData.data ?? []
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : [],
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Checks out a subscription and returns the payment intent result.
 *
 * @param {EndpointProps} param0 - The endpoint properties.
 * @param {string} param0.tag - The tag used for fetching the subscription checkout.
 * @returns {Promise<{ success: boolean; data?: SubscriptionSetupData | null; error?: string | null}>} A promise that resolves to an object containing the success status, payment intent result, and an error message if applicable.
 */
export async function checkOutSubscription({ tag}: EndpointProps): Promise<{ success: boolean; data?: SubscriptionSetupData | null; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<SubscriptionSetupData>>(tag,
        () => get<DefaultApiResponse<SubscriptionSetupData>>({ tag, url: `/subscription/checkout`}),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Charges the subscription immediately.
 * @param {EndpointProps & { data: { paymentMethodId: string } }} param0 - The endpoint properties and payment method ID.
 * @returns {Promise<{ success: boolean; data?: { paymentIntentClientSecret: string } | null; error?: string | null}>} A promise resolving to the charge result.
 */
export async function chargeSubscriptionNow({ tag, data }: EndpointProps & { data: { paymentMethodId: string } }): Promise<{ success: boolean; data?: { paymentIntentClientSecret: string } | null; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<{ paymentIntentClientSecret: string }>>(tag,
        () => post<DefaultApiResponse<{ paymentIntentClientSecret: string }>>({ tag, url: `/subscription/charge-now`, body: data }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Cancels a subscription.
 * @param {EndpointProps & { data: { reason: string } }} param0 - The endpoint properties and cancellation reason.
 * @returns {Promise<{ success: boolean; error?: string | null}>} A promise resolving to the cancellation result.
 */
export async function cancelSubscription({ tag, data }: EndpointProps & { data: { reason: string } }): Promise<{ success: boolean; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<null>>(tag,
        () => post<DefaultApiResponse<null>>({ tag, url: `/subscription/cancel`, body: data }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Previews a subscription change and returns the preview details.
 * 
 * @param {EndpointProps & { data: ChangePlan }} param - The endpoint properties and change plan data.
 * @returns {Promise<{ success: boolean; data?: SubscriptionChangePreview | null; error?: string | null}>} A promise resolving to the preview result.
 * @throws {Error} Throws an error if the data payload is missing.
 * @example
 * const { success, data, error } = await previewSubscriptionChange({ tag: 'PreviewChange', data: { newPlanId: 'plan_123', billingCycle: 'monthly' } })
 * if (success) {
 *   console.log('Preview:', data)
 * } else {
 *  console.error('Failed to preview change:', error)
 * }
 * @see ChangePlan - The interface representing the change plan data.
 * @see SubscriptionChangePreview - The interface representing the subscription change preview response.
 */
export async function previewSubscriptionChange({ tag, data }: EndpointProps & { data: ChangePlan }): Promise<{ success: boolean; data?: SubscriptionChangePreview | null; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<SubscriptionChangePreview>>(tag,
        () => post<DefaultApiResponse<SubscriptionChangePreview>>({ tag, url: `/subscription/change/plan/preview`, body: data }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Changes the subscription plan and returns the change result.
 * @param {EndpointProps & { data: ChangePlan }} param0 - The endpoint properties and change plan data.
 * @returns {Promise<{ success: boolean; data?: SubscriptionChangeResult | null; error?: string | null}>} A promise resolving to the change result.
 * @throws {Error} Throws an error if the data payload is missing.
 * @example
 * const { success, data, error } = await changeSubscription({ tag: 'ChangeSubscription', data: { newPlanId: 'plan_123', billingCycle: 'monthly' } })
 * if (success) {
 *  console.log('Change Result:', data)
 * } else {
 * console.error('Failed to change subscription:', error)
 * }
 * @see ChangePlan - The interface representing the change plan data.
 * @see SubscriptionChangeResult - The interface representing the subscription change result response.
 */
export async function changeSubscription({ tag, data }: EndpointProps & { data: ChangePlan }): Promise<{ success: boolean; data?: SubscriptionChangeResult | null; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<SubscriptionChangeResult>>(tag,
        () => post<DefaultApiResponse<SubscriptionChangeResult>>({ tag, url: `/subscription/change/plan`, body: data }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Resumes a canceled subscription and returns the resume result.
 * 
 * @param {EndpointProps} param - The endpoint properties.
 * @param {string} param.tag - The tag used for resuming the subscription.
 * @returns {Promise<{ success: boolean; data?: ResumeSubscriptionResult | null; error?: string | null}>} A promise resolving to the resume result.
 * @throws {Error} Throws an error if the fetch operation fails.
 * 
 * @example
 * const { success, data, error } = await resumeSubscription({ tag: 'ResumeSubscription' })
 * if (success) {
 *  console.log('Resume Result:', data)
 * } else {
 *  console.error('Failed to resume subscription:', error)
 * }
 * @see ResumeSubscriptionResult - The interface representing the resume subscription result response.
 */
export async function resumeSubscription({ tag }: EndpointProps): Promise<{ success: boolean; data?: ResumeSubscriptionResult | null; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<ResumeSubscriptionResult>>(tag,
        () => get<DefaultApiResponse<ResumeSubscriptionResult>>({ tag, url: `/subscription/resume`, body: {} }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Checks the status of a subscription plan by its name.
 * @param {EndpointProps & { name: keyof AccountType['name'] }} param0 - The endpoint properties and plan name.
 * @returns {Promise<{ success: boolean; data?: { isActive: boolean } | null; error?: string | null}>} A promise resolving to the plan status result.
 * @throws {Error} Throws an error if the fetch operation fails.
 * @example
 * 
 * const { success, data, error } = await checkPlanStatus({ tag: 'CheckPlanStatus', name: 'basic' })
 * if (success) {
 *   console.log('Plan Status:', data)
 * } else {
 *   console.error('Failed to check plan status:', error)
 * }
 */
export async function checkPlanStatus({ tag, name }: EndpointProps & { name: keyof AccountType['name'] }): Promise<{ success: boolean; data?: { active: boolean } | null; error?: string | null}> {
    const result = await fetchApiData<DefaultApiResponse<{ active: boolean }>>(tag,
        () => get<DefaultApiResponse<{ active: boolean }>>({ tag, url: `/subscription/plan/${String(name)}/status` }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/**
 * Checks the statuses of all subscription plans.
 * @param {EndpointProps} param0 - The endpoint properties.
 * @param {string} param0.tag - The tag used for fetching the plan statuses.
 * @returns {Promise<{ success: boolean; data?: Record<keyof AccountType['name'], { success: boolean; active: boolean }> | null; error?: string | null }>} A promise resolving to the plan statuses result.
 * @throws {Error} Throws an error if the fetch operation fails.
 * @example
 * const { success, data, error } = await checkAllPlanStatuses({ tag: 'CheckAllPlanStatuses' })
 * if (success) {
 *      console.log('All Plan Statuses:', data)
 *      // Example output:
 *      { 
 *          "free": { "success": true, "active": false },
 *          "basic": { "success": true, "active": true },
 *          "sapphire": { "success": true, "active": false },
 *          "gold": { "success": true, "active": false },
 *          "platinum": { "success": true, "active": false },
 *          "sandbox": { "success": true, "active": false }
 *      }
 * } else {
 *      console.error('Failed to check all plan statuses:', error)
 * }
 * @see AccountType - The interface representing the account type.
 */
export async function checkAllPlanStatuses({ tag }: EndpointProps): Promise<{ success: boolean; data?: Record <keyof AccountType['name'], { success: boolean; active: boolean }> | null; error?: string | null }> {
    const result = await fetchApiData<DefaultApiResponse<Record <keyof AccountType['name'], { active: boolean }>>>(tag,
        () => get<DefaultApiResponse<Record <keyof AccountType['name'], { active: boolean }>>>({ tag, url: `/subscription/plans/status` }),
        (responseData) => responseData.data ?? null
    )

    return {
        success: result.response.status === 'success',
        data: result.response.status === 'success' ? result.response.data : null,
        error: result.response.status === 'error' ? result.response.message : null
    }
}

/* eslint-enable @typescript-eslint/no-redundant-type-constituents */
/* eslint-enable @typescript-eslint/no-unsafe-return */
/* eslint-enable @typescript-eslint/no-unsafe-argument */